// src/pages/admin/TaxResearchRag.tsx
// Milestone 2: Full Tax Research RAG admin surface.
// Tabs: Estimate (dry-run cost), Runs, Inventory, Failed items, Config.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BookOpen, Calculator, RefreshCw, Play, Boxes, AlertTriangle, Settings,
  CheckCircle2, Circle, StopCircle, Search,
} from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import {
  listTaxRagSources,
  estimateTaxRagSource,
  runTaxRagSource,
  listTaxRagRuns,
  listTaxRagInventory,
  listTaxRagFailedItems,
  getTaxRagIndexStatus,
  getTaxRagCorpusStats,
  inspectTaxRagRetrieval,
  provisionTaxRagIndex,
  TaxRagSource,
  TaxRagEstimate,
  TaxRagRun,
  TaxRagRunSummary,
  TaxRagInventoryItem,
  TaxRagFailedItem,
  TaxRagIndexStatus,
  TaxRagCorpusStats,
  TaxRagInspectResult,
  TaxRagInspectChunk,
  RunMode,
} from '../../services/taxRag';

// ── Formatting helpers ───────────────────────────────────────────────────────

const MODEL_LABELS: Record<string, string> = {
  small: 'text-embedding-3-small (1536d)',
  large: 'text-embedding-3-large (3072d)',
};

function formatUsd(n: number): string {
  if (n >= 1)      return `$${n.toFixed(2)}`;
  if (n >= 0.01)   return `$${n.toFixed(3)}`;
  if (n >= 0.0001) return `$${n.toFixed(4)}`;
  if (n === 0)     return '$0';
  return `$${n.toExponential(2)}`;
}
const formatNumber = (n: number) => n.toLocaleString('en-US');
function formatBytes(n: number | undefined | null): string {
  if (n == null) return '—';
  if (n < 1024)        return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
function formatDate(s: string | null | undefined): string {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(); } catch { return s; }
}
function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}
function statusColor(status: string): string {
  switch (status) {
    case 'succeeded': return 'var(--good)';
    case 'partial':   return 'var(--warn)';
    case 'failed':    return 'var(--bad)';
    case 'running':   return 'var(--accent)';
    default:          return 'var(--muted)';
  }
}

const ERROR_TEXT  = 'var(--bad)';
const WARN_TEXT   = 'var(--warn)';
const CARD_BORDER = '1px solid var(--border)';

// ── Tabs ─────────────────────────────────────────────────────────────────────

type TabKey = 'estimate' | 'runs' | 'inventory' | 'inspector' | 'failed' | 'config';

const TABS: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: 'estimate',  label: 'Estimate',      icon: <Calculator size={14} /> },
  { key: 'runs',      label: 'Runs',          icon: <Play size={14} /> },
  { key: 'inventory', label: 'Inventory',     icon: <Boxes size={14} /> },
  { key: 'inspector', label: 'Inspector',     icon: <Search size={14} /> },
  { key: 'failed',    label: 'Failed items',  icon: <AlertTriangle size={14} /> },
  { key: 'config',    label: 'Config',        icon: <Settings size={14} /> },
];

// ── Top-level page ───────────────────────────────────────────────────────────

export default function TaxResearchRag() {
  const { ready, isAdmin } = useAuth();
  const [tab, setTab] = useState<TabKey>('estimate');
  const [sources, setSources] = useState<TaxRagSource[]>([]);
  const [sourceCounts, setSourceCounts] = useState<{ total: number; processed: number; unprocessed: number }>({ total: 0, processed: 0, unprocessed: 0 });
  const [sourcesError, setSourcesError] = useState<string | null>(null);
  const [sourcesLoading, setSourcesLoading] = useState(false);

  const refreshSources = useCallback(async () => {
    setSourcesLoading(true);
    try {
      const res = await listTaxRagSources();
      setSources(res.sources);
      setSourceCounts(res.counts);
      setSourcesError(null);
    } catch (e: any) {
      setSourcesError(e?.message || String(e));
    } finally {
      setSourcesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!ready || !isAdmin) return;
    refreshSources();
  }, [ready, isAdmin, refreshSources]);

  if (!ready) return null;
  if (!isAdmin) {
    return (
      <section className="card">
        <div className="hd"><div className="row"><div style={{ fontWeight: 760 }}>Admin • Tax Research RAG</div></div></div>
        <div className="bd"><div className="muted">You do not have access to this page.</div></div>
      </section>
    );
  }

  return (
    <div className="page-stack">
      <section className="card">
        <div className="hd">
          <div className="row">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ fontWeight: 760 }}>
                <BookOpen size={16} style={{ verticalAlign: -3, marginRight: 6 }} />
                Admin • Tax Research RAG
              </div>
              <div className="muted" style={{ fontSize: 13 }}>
                Ingest Tax Analysts NewsML-G2 feeds into Azure AI Search. Runs
                can be triggered from here or by a scheduler (Logic App) hitting{' '}
                <code>/api/tax-rag-run</code> with the shared-secret header.
              </div>
            </div>
          </div>
          <div className="bd" style={{ paddingTop: 0 }}>
            <TabNav current={tab} onChange={setTab} />
          </div>
        </div>
      </section>

      {sourcesError && (
        <section className="card"><div className="bd"><div className="muted" style={{ color: ERROR_TEXT }}>Failed to load sources: {sourcesError}</div></div></section>
      )}

      {tab === 'estimate'  && <EstimateTab  sources={sources} counts={sourceCounts} loading={sourcesLoading} onRefresh={refreshSources} onGoToRuns={() => setTab('runs')} />}
      {tab === 'runs'      && <RunsTab      sources={sources} counts={sourceCounts} loading={sourcesLoading} onRefresh={refreshSources} />}
      {tab === 'inventory' && <InventoryTab />}
      {tab === 'inspector' && <InspectorTab />}
      {tab === 'failed'    && <FailedItemsTab />}
      {tab === 'config'    && <ConfigTab />}
    </div>
  );
}

function TabNav({ current, onChange }: { current: TabKey; onChange: (k: TabKey) => void }) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
      {TABS.map((t) => {
        const active = t.key === current;
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onChange(t.key)}
            className={active ? 'btn primary' : 'btn'}
          >
            {t.icon}
            <span style={{ marginLeft: 6 }}>{t.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// Shared option style so the native OS popup renders legibly in dark mode.
const OPTION_STYLE: React.CSSProperties = { backgroundColor: 'var(--bg)', color: 'var(--text)' };

// ── Tab: Estimate ────────────────────────────────────────────────────────────

function EstimateTab({
  sources, counts, loading, onRefresh, onGoToRuns,
}: {
  sources: TaxRagSource[];
  counts: { total: number; processed: number; unprocessed: number };
  loading: boolean;
  onRefresh: () => void;
  onGoToRuns: () => void;
}) {
  const [estimates, setEstimates] = useState<Record<string, TaxRagEstimate>>({});
  const [pending, setPending]     = useState<Record<string, boolean>>({});
  const [errors, setErrors]       = useState<Record<string, string>>({});
  const [showOnlyUnprocessed, setShowOnlyUnprocessed] = useState(true);
  const [filterText, setFilterText] = useState('');
  const [skipAlreadyEstimated, setSkipAlreadyEstimated] = useState(true);
  const [batchActive, setBatchActive] = useState(false);
  const [batchTotal, setBatchTotal] = useState(0);
  const [batchDone, setBatchDone] = useState(0);
  const [batchCurrent, setBatchCurrent] = useState<string | null>(null);
  const [batchCancelRequested, setBatchCancelRequested] = useState(false);
  const cancelRef = useRef(false);

  const filtered = useMemo(() => {
    const f = filterText.trim().toLowerCase();
    return sources.filter((s) => {
      if (showOnlyUnprocessed && s.processed) return false;
      if (f && !s.filename.toLowerCase().includes(f) && !s.label.toLowerCase().includes(f)) return false;
      return true;
    });
  }, [sources, showOnlyUnprocessed, filterText]);

  // Aggregated cost across estimates we've computed in this session — refreshes
  // as the batch progresses.
  const aggregate = useMemo(() => {
    const keys = filtered.map((s) => s.key).filter((k) => estimates[k]);
    let items = 0, chunks = 0, tokens = 0, smallStd = 0, smallBatch = 0, largeStd = 0, largeBatch = 0;
    for (const k of keys) {
      const e = estimates[k];
      items      += e.stats.items.total;
      chunks     += e.stats.chunks.total;
      tokens     += e.stats.tokens.total;
      smallStd   += e.cost.small.standard;
      smallBatch += e.cost.small.batch;
      largeStd   += e.cost.large.standard;
      largeBatch += e.cost.large.batch;
    }
    return { count: keys.length, items, chunks, tokens, smallStd, smallBatch, largeStd, largeBatch };
  }, [filtered, estimates]);

  async function runEstimate(src: TaxRagSource, bustCache = false) {
    setPending((p) => ({ ...p, [src.key]: true }));
    setErrors((e) => { const n = { ...e }; delete n[src.key]; return n; });
    try {
      const res = await estimateTaxRagSource(src.url, { bustCache });
      setEstimates((prev) => ({ ...prev, [src.key]: res }));
    } catch (e: any) {
      setErrors((prev) => ({ ...prev, [src.key]: e?.message || String(e) }));
    } finally {
      setPending((p) => ({ ...p, [src.key]: false }));
    }
  }

  async function estimateAllVisible() {
    const queue = filtered.filter((s) => !skipAlreadyEstimated || !estimates[s.key]);
    if (queue.length === 0) return;
    const ok = window.confirm(
      `Estimate ${queue.length} file(s)? Each file fetches the zip from blob, parses it, and computes projected chunks/tokens/cost. The estimate endpoint caches results by file hash so re-runs are cheap.`
    );
    if (!ok) return;

    cancelRef.current = false;
    setBatchCancelRequested(false);
    setBatchActive(true);
    setBatchTotal(queue.length);
    setBatchDone(0);
    setBatchCurrent(null);

    for (let i = 0; i < queue.length; i++) {
      if (cancelRef.current) break;
      const src = queue[i];
      setBatchCurrent(src.filename);
      await runEstimate(src, false);
      setBatchDone(i + 1);
    }

    setBatchActive(false);
    setBatchCurrent(null);
  }

  function cancelBatch() {
    cancelRef.current = true;
    setBatchCancelRequested(true);
  }

  const estimateableCount = filtered.filter((s) => !skipAlreadyEstimated || !estimates[s.key]).length;

  return (
    <>
      <section className="card">
        <div className="hd">
          <div className="row">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ fontWeight: 760 }}>Sources in <code>taxNotes/</code></div>
              <div className="muted" style={{ fontSize: 13 }}>
                {counts.total} total · <span style={{ color: 'var(--good)' }}>{counts.processed} processed</span> · <span style={{ color: 'var(--warn)' }}>{counts.unprocessed} unprocessed</span>
              </div>
            </div>
          </div>
        </div>
        <div className="bd">
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ fontSize: 13 }}>
              <input type="checkbox" checked={showOnlyUnprocessed} onChange={(e) => setShowOnlyUnprocessed(e.target.checked)} /> Only unprocessed
            </label>
            <input
              type="text"
              placeholder="Filter by filename"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              style={{ minWidth: 240 }}
            />
            <label style={{ fontSize: 13 }} title="Don't re-estimate files that already have a result in this session">
              <input type="checkbox" checked={skipAlreadyEstimated} onChange={(e) => setSkipAlreadyEstimated(e.target.checked)} /> Skip already estimated
            </label>
            <button className="btn" type="button" onClick={onRefresh} disabled={loading || batchActive}>
              <RefreshCw size={14} /> {loading ? 'Loading…' : 'Refresh'}
            </button>
            {!batchActive && estimateableCount > 0 && (
              <button className="btn primary" type="button" onClick={estimateAllVisible}>
                <Calculator size={14} /> Estimate all visible ({estimateableCount})
              </button>
            )}
            {batchActive && (
              <button className="btn" type="button" onClick={cancelBatch}>
                <StopCircle size={14} /> Stop after current
              </button>
            )}
            <div className="muted" style={{ fontSize: 13 }}>
              Showing {filtered.length} of {sources.length}
            </div>
          </div>

          {batchActive && (
            <div style={{ marginTop: 14, padding: 10, border: CARD_BORDER, borderRadius: 10 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                Estimating — {batchDone} / {batchTotal} done
                {batchCancelRequested && <span style={{ color: WARN_TEXT, marginLeft: 8 }}>(stopping after current)</span>}
              </div>
              <div className="muted" style={{ fontSize: 12 }}>
                Current: <code>{batchCurrent || '—'}</code>
              </div>
              <div style={{
                marginTop: 8, height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.07)', overflow: 'hidden',
              }}>
                <div style={{
                  width: `${batchTotal ? (batchDone / batchTotal) * 100 : 0}%`,
                  height: '100%', background: 'var(--accent)', transition: 'width 0.2s',
                }} />
              </div>
            </div>
          )}

          {aggregate.count > 0 && (
            <div style={{ marginTop: 14, padding: 10, border: CARD_BORDER, borderRadius: 10 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                Combined projection across {aggregate.count} estimated file(s)
              </div>
              <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
                items {formatNumber(aggregate.items)} · chunks {formatNumber(aggregate.chunks)} · tokens {formatNumber(aggregate.tokens)}
              </div>
              <table className="tx-rag-table" style={{ maxWidth: 520 }}>
                <thead><tr><th>Model</th><th style={{ textAlign: 'right' }}>Standard</th><th style={{ textAlign: 'right' }}>Batch</th></tr></thead>
                <tbody>
                  <tr><td>{MODEL_LABELS.small}</td><td style={{ textAlign: 'right' }}>{formatUsd(aggregate.smallStd)}</td><td style={{ textAlign: 'right' }}>{formatUsd(aggregate.smallBatch)}</td></tr>
                  <tr><td>{MODEL_LABELS.large}</td><td style={{ textAlign: 'right' }}>{formatUsd(aggregate.largeStd)}</td><td style={{ textAlign: 'right' }}>{formatUsd(aggregate.largeBatch)}</td></tr>
                </tbody>
              </table>
              <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
                The pipeline uses standard pricing (synchronous embeddings API). The "Batch" column is projection-only — see Config for details.
              </div>
              <div style={{ marginTop: 10 }}>
                <button className="btn primary" type="button" onClick={onGoToRuns}>
                  <Play size={14} /> Switch to Runs tab to execute →
                </button>
              </div>
              <TableStyles />
            </div>
          )}
        </div>
      </section>

      {filtered.length === 0 && (
        <section className="card">
          <div className="bd"><div className="muted">No sources match the current filter.</div></div>
        </section>
      )}

      {filtered.map((src) => (
        <section key={src.key} className="card">
          <div className="hd">
            <div className="row">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ fontWeight: 760, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {src.processed
                    ? <CheckCircle2 size={14} style={{ color: 'var(--good)' }} />
                    : <Circle size={14} style={{ color: 'var(--warn)' }} />}
                  {src.label}
                </div>
                <div className="muted" style={{ fontSize: 13 }}>{src.description}</div>
                <div className="muted" style={{ fontSize: 11, wordBreak: 'break-all' }}>{src.url}</div>
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button className="btn" type="button" disabled={pending[src.key] || batchActive} onClick={() => runEstimate(src, false)}>
                  <Calculator size={16} /> {pending[src.key] ? 'Estimating…' : estimates[src.key] ? 'Re-estimate' : 'Estimate'}
                </button>
                {estimates[src.key] && (
                  <button className="btn" type="button" disabled={pending[src.key] || batchActive} onClick={() => runEstimate(src, true)}>
                    <RefreshCw size={16} /> Bust cache
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="bd">
            {errors[src.key] && <div className="muted" style={{ color: ERROR_TEXT }}>Error: {errors[src.key]}</div>}
            {!estimates[src.key] && !errors[src.key] && !pending[src.key] && (
              <div className="muted">Click <strong>Estimate</strong> to fetch the zip, parse it, and see the cost to embed.</div>
            )}
            {estimates[src.key] && <EstimateView estimate={estimates[src.key]} />}
          </div>
        </section>
      ))}
    </>
  );
}

function EstimateView({ estimate }: { estimate: TaxRagEstimate }) {
  const { stats, cost, wallClockEstimate } = estimate;
  const docTypeRows = useMemo(
    () => Object.entries(stats.items.byDocType).sort((a, b) => b[1] - a[1]),
    [stats],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="muted" style={{ fontSize: 12 }}>
        {estimate.cached ? 'Cached result' : 'Computed'} in {estimate.elapsedMs} ms ·{' '}
        xml {formatBytes(estimate.xmlBytes)} · zip {formatBytes(estimate.zipBytes)} ·{' '}
        file hash <code style={{ fontSize: 11 }}>{estimate.fileHash.slice(0, 12)}…</code>
      </div>

      <div className="kv">
        <div className="field"><div className="k">newsItems</div>         <div className="v">{formatNumber(stats.items.total)}</div></div>
        <div className="field"><div className="k">Updates (v &gt; 1)</div><div className="v">{formatNumber(stats.items.updates)}</div></div>
        <div className="field"><div className="k">Items w/ no body</div>  <div className="v">{formatNumber(stats.itemsWithNoBody)}</div></div>
        <div className="field"><div className="k">PDF attachments</div>   <div className="v">{formatNumber(stats.attachments.pdf)}</div></div>
        <div className="field"><div className="k">Image attachments</div> <div className="v">{formatNumber(stats.attachments.image)}</div></div>
      </div>

      <div className="kv">
        <div className="field"><div className="k">Total chunks</div>   <div className="v">{formatNumber(stats.chunks.total)}</div></div>
        <div className="field"><div className="k">Body chunks</div>    <div className="v">{formatNumber(stats.chunks.body)}</div></div>
        <div className="field"><div className="k">Summary chunks</div> <div className="v">{formatNumber(stats.chunks.summary)}</div></div>
        <div className="field"><div className="k">Footnote chunks</div><div className="v">{formatNumber(stats.chunks.footnote)}</div></div>
      </div>

      <div className="kv">
        <div className="field"><div className="k">Total tokens</div>   <div className="v">{formatNumber(stats.tokens.total)}</div></div>
        <div className="field"><div className="k">Body tokens</div>    <div className="v">{formatNumber(stats.tokens.body)}</div></div>
        <div className="field"><div className="k">Header tokens</div>  <div className="v">{formatNumber(stats.tokens.headers)}</div></div>
      </div>

      <div>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Embedding cost (USD)</div>
        <table className="tx-rag-table">
          <thead><tr><th>Model</th><th style={{ textAlign: 'right' }}>Standard</th><th style={{ textAlign: 'right' }}>Batch (50% off)</th></tr></thead>
          <tbody>
            <tr><td>{MODEL_LABELS.small}</td><td style={{ textAlign: 'right' }}>{formatUsd(cost.small.standard)}</td><td style={{ textAlign: 'right' }}>{formatUsd(cost.small.batch)}</td></tr>
            <tr><td>{MODEL_LABELS.large}</td><td style={{ textAlign: 'right' }}>{formatUsd(cost.large.standard)}</td><td style={{ textAlign: 'right' }}>{formatUsd(cost.large.batch)}</td></tr>
          </tbody>
        </table>
        <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
          Estimated wall clock at concurrency {wallClockEstimate.concurrency}, batch size {wallClockEstimate.batchSize}: ~{wallClockEstimate.seconds}s
        </div>
      </div>

      {docTypeRows.length > 0 && (
        <div>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Items by document type</div>
          <table className="tx-rag-table">
            <thead><tr><th>Document type</th><th style={{ textAlign: 'right' }}>Count</th></tr></thead>
            <tbody>{docTypeRows.map(([t, c]) => (<tr key={t}><td>{t}</td><td style={{ textAlign: 'right' }}>{formatNumber(c)}</td></tr>))}</tbody>
          </table>
        </div>
      )}
      <TableStyles />
    </div>
  );
}

// ── Tab: Runs ────────────────────────────────────────────────────────────────

type BatchResult = {
  src: TaxRagSource;
  summary: TaxRagRunSummary | null;
  error: string | null;
};

function RunsTab({
  sources, counts, loading: sourcesLoading, onRefresh,
}: {
  sources: TaxRagSource[];
  counts: { total: number; processed: number; unprocessed: number };
  loading: boolean;
  onRefresh: () => Promise<void> | void;
}) {
  const [runs, setRuns] = useState<TaxRagRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [runPending, setRunPending] = useState<Record<string, boolean>>({});
  const [runMode, setRunMode] = useState<RunMode>('full');
  const [embeddingModel, setEmbeddingModel] = useState<'text-embedding-3-small' | 'text-embedding-3-large'>('text-embedding-3-large');
  const [indexStatus, setIndexStatus] = useState<TaxRagIndexStatus | null>(null);
  const [lastSummary, setLastSummary] = useState<TaxRagRunSummary | null>(null);
  const [showOnlyUnprocessed, setShowOnlyUnprocessed] = useState(true);
  const [filterText, setFilterText] = useState('');
  const [batchActive, setBatchActive] = useState(false);
  const [batchTotal, setBatchTotal] = useState(0);
  const [batchDone, setBatchDone] = useState(0);
  const [batchCurrent, setBatchCurrent] = useState<string | null>(null);
  const [batchResults, setBatchResults] = useState<BatchResult[]>([]);
  const [batchCancelRequested, setBatchCancelRequested] = useState(false);
  const cancelRef = useRef(false);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await listTaxRagRuns(50);
      setRuns(r.runs);
    } catch (e: any) { setError(e?.message || String(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Load index status so we can warn on model ↔ dimension mismatch.
  useEffect(() => {
    let cancelled = false;
    getTaxRagIndexStatus()
      .then((s) => { if (!cancelled) setIndexStatus(s); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const selectedDims = embeddingModel === 'text-embedding-3-large' ? 3072 : 1536;
  const indexDims = indexStatus && indexStatus.configured ? indexStatus.vectorDimensions ?? null : null;
  const indexName = indexStatus && indexStatus.configured ? indexStatus.indexName : null;
  const dimMismatch = runMode === 'full' && indexDims != null && indexDims !== selectedDims;

  const filtered = useMemo(() => {
    const f = filterText.trim().toLowerCase();
    return sources.filter((s) => {
      if (showOnlyUnprocessed && s.processed) return false;
      if (f && !s.filename.toLowerCase().includes(f) && !s.label.toLowerCase().includes(f)) return false;
      return true;
    });
  }, [sources, showOnlyUnprocessed, filterText]);

  async function runNow(src: TaxRagSource) {
    setRunPending((p) => ({ ...p, [src.key]: true }));
    setError(null); setLastSummary(null);
    try {
      const s = await runTaxRagSource({
        sourceUrl: src.url,
        sourceName: src.label,
        mode: runMode,
        embeddingModel: runMode === 'full' ? embeddingModel : undefined,
      });
      setLastSummary(s);
      await reload();
      await onRefresh();
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setRunPending((p) => ({ ...p, [src.key]: false }));
    }
  }

  async function runBatch() {
    const queue = sources.filter((s) => !s.processed);
    if (queue.length === 0) return;
    const verb = runMode === 'full' ? `embed (${embeddingModel}) and index` : 'dry-run';
    let confirmMsg = `Run ${verb} on ${queue.length} unprocessed file(s)?\n\n`;
    if (runMode === 'full') {
      confirmMsg += `Full mode hits OpenAI for embeddings and Azure AI Search for indexing — this may take a long time and incur cost. Each file processes serially.\n\n`;
      if (dimMismatch) {
        confirmMsg +=
          `⚠ WARNING: index "${indexName}" is ${indexDims}d but you've picked ${embeddingModel} (${selectedDims}d). ` +
          `The pipeline will fail at index time. Provision a new index name on the Config tab first.`;
      }
    } else {
      confirmMsg += 'Dry-run only parses and chunks; no embedding or indexing happens.';
    }
    const ok = window.confirm(confirmMsg);
    if (!ok) return;

    cancelRef.current = false;
    setBatchCancelRequested(false);
    setError(null);
    setLastSummary(null);
    setBatchActive(true);
    setBatchTotal(queue.length);
    setBatchDone(0);
    setBatchCurrent(null);
    setBatchResults([]);

    for (let i = 0; i < queue.length; i++) {
      if (cancelRef.current) break;
      const src = queue[i];
      setBatchCurrent(src.filename);
      try {
        const summary = await runTaxRagSource({
          sourceUrl: src.url,
          sourceName: src.label,
          mode: runMode,
          embeddingModel: runMode === 'full' ? embeddingModel : undefined,
        });
        setBatchResults((prev) => [...prev, { src, summary, error: null }]);
      } catch (e: any) {
        setBatchResults((prev) => [...prev, { src, summary: null, error: e?.message || String(e) }]);
      }
      setBatchDone(i + 1);
    }

    setBatchActive(false);
    setBatchCurrent(null);
    await reload();
    await onRefresh();
  }

  function cancelBatch() {
    cancelRef.current = true;
    setBatchCancelRequested(true);
  }

  const unprocessedCount = counts.unprocessed;

  return (
    <>
      <section className="card">
        <div className="hd">
          <div className="row">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ fontWeight: 760 }}>Run pipeline</div>
              <div className="muted" style={{ fontSize: 13 }}>
                <strong>Full</strong> embeds + indexes into Azure AI Search + supersedes prior versions.{' '}
                <strong>Dry-run</strong> parses + chunks + projects cost, no OpenAI or AAS calls.
                Auto-demoted to dry-run if AAS env vars aren't set.
              </div>
              <div className="muted" style={{ fontSize: 13 }}>
                {counts.total} files in <code>taxNotes/</code> · <span style={{ color: 'var(--good)' }}>{counts.processed} processed</span> · <span style={{ color: 'var(--warn)' }}>{counts.unprocessed} unprocessed</span>
              </div>
            </div>
          </div>
        </div>
        <div className="bd">
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
            <label className="muted" style={{ fontSize: 13 }}>Mode:</label>
            <select value={runMode} onChange={(e) => setRunMode(e.target.value as RunMode)} disabled={batchActive}>
              <option value="full"    style={OPTION_STYLE}>Full (embed + index)</option>
              <option value="dry-run" style={OPTION_STYLE}>Dry run (no spend)</option>
            </select>
            <label className="muted" style={{ fontSize: 13 }}>Model:</label>
            <select
              value={embeddingModel}
              onChange={(e) => setEmbeddingModel(e.target.value as typeof embeddingModel)}
              disabled={batchActive || runMode !== 'full'}
            >
              <option value="text-embedding-3-small" style={OPTION_STYLE}>text-embedding-3-small (1536d)</option>
              <option value="text-embedding-3-large" style={OPTION_STYLE}>text-embedding-3-large (3072d)</option>
            </select>
            <label style={{ fontSize: 13 }}>
              <input type="checkbox" checked={showOnlyUnprocessed} onChange={(e) => setShowOnlyUnprocessed(e.target.checked)} /> Only unprocessed
            </label>
            <input
              type="text"
              placeholder="Filter by filename"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              style={{ minWidth: 220 }}
            />
            <button className="btn" type="button" onClick={() => { reload(); onRefresh(); }} disabled={loading || sourcesLoading}>
              <RefreshCw size={14} /> Refresh
            </button>
            {!batchActive && unprocessedCount > 0 && (
              <button className="btn primary" type="button" onClick={runBatch}>
                <Play size={14} /> Run all unprocessed ({unprocessedCount}) — {runMode}
              </button>
            )}
            {batchActive && (
              <button className="btn" type="button" onClick={cancelBatch}>
                <StopCircle size={14} /> Stop after current
              </button>
            )}
            <div className="muted" style={{ fontSize: 13 }}>
              Showing {filtered.length} of {sources.length}
            </div>
          </div>

          {dimMismatch && (
            <div style={{
              marginBottom: 14, padding: 10, border: CARD_BORDER, borderRadius: 10,
              background: 'rgba(255, 180, 0, 0.08)',
            }}>
              <div style={{ fontWeight: 600, color: WARN_TEXT }}>
                ⚠ Vector dimension mismatch
              </div>
              <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                Index <code>{indexName}</code> is provisioned for <strong>{indexDims}d</strong>,
                but <code>{embeddingModel}</code> produces <strong>{selectedDims}d</strong> vectors.
                The pipeline will fail at index time. Either pick a model that matches the existing
                index, or go to the <strong>Config</strong> tab, change <code>AZURE_SEARCH_INDEX_NAME</code>{' '}
                to a new name (e.g. <code>tax-authority-v2</code>), and click <strong>Create / update index</strong>{' '}
                with the {embeddingModel === 'text-embedding-3-large' ? 'large' : 'small'} model.
              </div>
            </div>
          )}

          {batchActive && (
            <div style={{ marginBottom: 14, padding: 10, border: CARD_BORDER, borderRadius: 10 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                Batch run in progress — {batchDone} / {batchTotal} done
                {batchCancelRequested && <span style={{ color: WARN_TEXT, marginLeft: 8 }}>(stopping after current)</span>}
              </div>
              <div className="muted" style={{ fontSize: 12 }}>
                Current: <code>{batchCurrent || '—'}</code>
              </div>
              <div style={{
                marginTop: 8, height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.07)', overflow: 'hidden',
              }}>
                <div style={{
                  width: `${batchTotal ? (batchDone / batchTotal) * 100 : 0}%`,
                  height: '100%', background: 'var(--accent)', transition: 'width 0.2s',
                }} />
              </div>
            </div>
          )}

          {batchResults.length > 0 && !batchActive && (
            <BatchSummary results={batchResults} />
          )}

          <div style={{ overflowX: 'auto' }}>
            <table className="tx-rag-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>File</th>
                  <th>Kind</th>
                  <th style={{ textAlign: 'right' }}>Size</th>
                  <th>Last run</th>
                  <th style={{ textAlign: 'right' }}>Items</th>
                  <th style={{ textAlign: 'right' }}>Cost</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((src) => {
                  const lr = src.latestRun;
                  return (
                    <tr key={src.key}>
                      <td>
                        {src.processed
                          ? <span style={{ color: 'var(--good)' }}>● processed</span>
                          : lr
                            ? <span style={{ color: statusColor(lr.status) }}>● {lr.status}</span>
                            : <span style={{ color: 'var(--warn)' }}>○ unprocessed</span>}
                      </td>
                      <td title={src.url} style={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {src.filename}
                      </td>
                      <td>{src.kind}</td>
                      <td style={{ textAlign: 'right' }}>{formatBytes(src.size)}</td>
                      <td>{lr?.startedAt ? formatDate(lr.startedAt) : '—'}</td>
                      <td style={{ textAlign: 'right' }}>
                        {lr ? `${formatNumber(lr.itemsCreated + lr.itemsUpdated)}/${formatNumber(lr.itemsTotal)}` : '—'}
                      </td>
                      <td style={{ textAlign: 'right' }}>{lr ? formatUsd(lr.estimatedCostUsd) : '—'}</td>
                      <td>
                        <button
                          className="btn"
                          type="button"
                          disabled={runPending[src.key] || batchActive}
                          onClick={() => runNow(src)}
                        >
                          <Play size={12} /> {runPending[src.key] ? '…' : 'Run'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={8} className="muted" style={{ padding: 12 }}>No sources match the current filter.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {lastSummary && (
            <div style={{ marginTop: 14, padding: 10, border: CARD_BORDER, borderRadius: 10 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                <span style={{ color: statusColor(lastSummary.status) }}>●</span>{' '}
                Last run: {lastSummary.status} ({lastSummary.mode})
              </div>
              <div className="muted" style={{ fontSize: 12 }}>
                created {lastSummary.counts.itemsCreated} · updated {lastSummary.counts.itemsUpdated} · skipped {lastSummary.counts.itemsSkipped} · failed {lastSummary.counts.itemsFailed} ·{' '}
                chunks upserted {formatNumber(lastSummary.counts.chunksUpserted)} · deleted {formatNumber(lastSummary.counts.chunksDeleted)} ·{' '}
                tokens {formatNumber(lastSummary.counts.tokensEmbedded)} · cost {formatUsd(lastSummary.counts.estimatedCostUsd)}
              </div>
              {lastSummary.errorSummary && (
                <div style={{ color: ERROR_TEXT, fontSize: 12, marginTop: 6 }}>Error: {lastSummary.errorSummary}</div>
              )}
            </div>
          )}
        </div>
      </section>

      <section className="card">
        <div className="hd"><div className="row"><div style={{ fontWeight: 760 }}>Recent runs</div></div></div>
        <div className="bd">
          {error && <div className="muted" style={{ color: ERROR_TEXT }}>Error: {error}</div>}
          {loading && !runs.length && <div className="muted">Loading…</div>}
          {!loading && !runs.length && !error && <div className="muted">No runs yet. Kick one off above or via your scheduler.</div>}
          {runs.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table className="tx-rag-table">
                <thead>
                  <tr>
                    <th>Status</th><th>Source</th><th>Mode</th><th>Started</th><th style={{ textAlign: 'right' }}>Duration</th>
                    <th style={{ textAlign: 'right' }}>Created</th><th style={{ textAlign: 'right' }}>Updated</th><th style={{ textAlign: 'right' }}>Skipped</th><th style={{ textAlign: 'right' }}>Failed</th>
                    <th style={{ textAlign: 'right' }}>Chunks ±</th><th style={{ textAlign: 'right' }}>Tokens</th><th style={{ textAlign: 'right' }}>Cost</th>
                    <th>Model</th><th>By</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r) => (
                    <tr key={r.run_id}>
                      <td><span style={{ color: statusColor(r.status) }}>●</span> {r.status}</td>
                      <td>{r.source_name}</td>
                      <td>{r.mode}</td>
                      <td>{formatDate(r.started_at)}</td>
                      <td style={{ textAlign: 'right' }}>{formatDuration(r.duration_seconds)}</td>
                      <td style={{ textAlign: 'right' }}>{formatNumber(r.items_created)}</td>
                      <td style={{ textAlign: 'right' }}>{formatNumber(r.items_updated)}</td>
                      <td style={{ textAlign: 'right' }}>{formatNumber(r.items_skipped)}</td>
                      <td style={{ textAlign: 'right', color: r.items_failed > 0 ? ERROR_TEXT : undefined }}>{formatNumber(r.items_failed)}</td>
                      <td style={{ textAlign: 'right' }}>+{formatNumber(r.chunks_upserted)} / −{formatNumber(r.chunks_deleted)}</td>
                      <td style={{ textAlign: 'right' }}>{formatNumber(r.tokens_embedded)}</td>
                      <td style={{ textAlign: 'right' }}>{formatUsd(r.estimated_cost_usd)}</td>
                      <td>{r.embedding_model || '—'}</td>
                      <td>{r.triggered_by || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
      <TableStyles />
    </>
  );
}

function BatchSummary({ results }: { results: BatchResult[] }) {
  const totals = useMemo(() => {
    let ok = 0, partial = 0, failed = 0, errored = 0;
    let itemsCreated = 0, itemsUpdated = 0, itemsFailed = 0, cost = 0;
    for (const r of results) {
      if (r.error) { errored++; continue; }
      const s = r.summary!;
      if (s.status === 'succeeded') ok++;
      else if (s.status === 'partial') partial++;
      else failed++;
      itemsCreated += s.counts.itemsCreated;
      itemsUpdated += s.counts.itemsUpdated;
      itemsFailed  += s.counts.itemsFailed;
      cost         += s.counts.estimatedCostUsd;
    }
    return { ok, partial, failed, errored, itemsCreated, itemsUpdated, itemsFailed, cost };
  }, [results]);

  return (
    <div style={{ marginBottom: 14, padding: 10, border: CARD_BORDER, borderRadius: 10 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>
        Batch finished — {results.length} file(s) processed
      </div>
      <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
        <span style={{ color: 'var(--good)' }}>{totals.ok} succeeded</span> ·{' '}
        <span style={{ color: 'var(--warn)' }}>{totals.partial} partial</span> ·{' '}
        <span style={{ color: ERROR_TEXT }}>{totals.failed} failed</span> ·{' '}
        <span style={{ color: ERROR_TEXT }}>{totals.errored} request errors</span> ·{' '}
        items created {formatNumber(totals.itemsCreated)} · updated {formatNumber(totals.itemsUpdated)} · failed {formatNumber(totals.itemsFailed)} ·{' '}
        total cost {formatUsd(totals.cost)}
      </div>
      {(totals.failed > 0 || totals.errored > 0) && (
        <details>
          <summary className="muted" style={{ fontSize: 12, cursor: 'pointer' }}>
            Show errored files
          </summary>
          <div style={{ marginTop: 6, fontSize: 12 }}>
            {results
              .filter((r) => r.error || (r.summary && r.summary.status !== 'succeeded'))
              .map((r, i) => (
                <div key={i} style={{ marginTop: 4 }}>
                  <code>{r.src.filename}</code> —{' '}
                  {r.error
                    ? <span style={{ color: ERROR_TEXT }}>request error: {r.error}</span>
                    : <span style={{ color: r.summary!.status === 'partial' ? WARN_TEXT : ERROR_TEXT }}>
                        {r.summary!.status} (failed {r.summary!.counts.itemsFailed})
                        {r.summary!.errorSummary ? ` — ${r.summary!.errorSummary}` : ''}
                      </span>}
                </div>
              ))}
          </div>
        </details>
      )}
    </div>
  );
}

// ── Tab: Inventory ───────────────────────────────────────────────────────────

function InventoryTab() {
  const [items, setItems] = useState<TaxRagInventoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [docType, setDocType] = useState('');
  const [page, setPage] = useState(0);
  const [corpus, setCorpus] = useState<TaxRagCorpusStats | null>(null);
  const [corpusError, setCorpusError] = useState<string | null>(null);
  const pageSize = 50;

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await listTaxRagInventory({
        limit: pageSize,
        offset: page * pageSize,
        q: q || undefined,
        documentType: docType || undefined,
      });
      setItems(res.items);
      setTotal(res.total);
    } catch (e: any) { setError(e?.message || String(e)); }
    finally { setLoading(false); }
  }, [page, q, docType]);

  const reloadCorpus = useCallback(async () => {
    try {
      const s = await getTaxRagCorpusStats();
      setCorpus(s);
      setCorpusError(null);
    } catch (e: any) {
      setCorpusError(e?.message || String(e));
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { reloadCorpus(); }, [reloadCorpus]);

  const maxPage = Math.max(0, Math.ceil(total / pageSize) - 1);

  return (
    <>
      <TierBreakdownCard
        corpus={corpus}
        error={corpusError}
        onReload={reloadCorpus}
        onFilterDocType={(dt) => { setDocType(dt); setPage(0); }}
      />
    <section className="card">
      <div className="hd">
        <div className="row">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ fontWeight: 760 }}>Inventory</div>
            <div className="muted" style={{ fontSize: 13 }}>
              What's currently indexed. Source of truth = dbo.tax_rag_ingest_state.
            </div>
          </div>
        </div>
      </div>
      <div className="bd">
        <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Search headline / guid / rid"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(0); }}
            style={{ minWidth: 240 }}
          />
          <input
            type="text"
            placeholder="Filter by document type"
            value={docType}
            onChange={(e) => { setDocType(e.target.value); setPage(0); }}
            style={{ minWidth: 200 }}
          />
          <button className="btn" type="button" onClick={reload}><RefreshCw size={14} /> Refresh</button>
          <div className="muted" style={{ alignSelf: 'center', fontSize: 13 }}>
            {formatNumber(total)} total
          </div>
        </div>

        {error && <div className="muted" style={{ color: ERROR_TEXT }}>Error: {error}</div>}
        {loading && !items.length && <div className="muted">Loading…</div>}
        {!loading && !items.length && !error && <div className="muted">No items indexed yet. Run a pipeline on the Runs tab.</div>}

        {items.length > 0 && (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table className="tx-rag-table">
                <thead>
                  <tr>
                    <th>Doc type</th><th>Headline</th><th>RID</th><th>Ver</th><th style={{ textAlign: 'right' }}>Chunks</th>
                    <th>Channel</th><th>Ingested</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.guid}>
                      <td>{it.document_type || '—'}</td>
                      <td title={it.guid}>{it.headline || it.guid}</td>
                      <td>{it.rid || '—'}</td>
                      <td>{it.current_version}</td>
                      <td style={{ textAlign: 'right' }}>{formatNumber(it.chunk_count)}</td>
                      <td>{it.channel || '—'}</td>
                      <td>{formatDate(it.ingested_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
              <button className="btn" disabled={page <= 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Prev</button>
              <div className="muted" style={{ fontSize: 13 }}>Page {page + 1} / {maxPage + 1}</div>
              <button className="btn" disabled={page >= maxPage} onClick={() => setPage((p) => Math.min(maxPage, p + 1))}>Next</button>
            </div>
          </>
        )}
        <TableStyles />
      </div>
    </section>
    </>
  );
}

function TierBreakdownCard({
  corpus, error, onReload, onFilterDocType,
}: {
  corpus: TaxRagCorpusStats | null;
  error: string | null;
  onReload: () => void;
  onFilterDocType: (docType: string) => void;
}) {
  const tierColor = (tier: number) => {
    if (tier <= 2) return 'var(--good)';
    if (tier <= 4) return 'var(--accent)';
    if (tier <= 6) return 'var(--warn)';
    return 'var(--muted)';
  };

  return (
    <section className="card">
      <div className="hd">
        <div className="row">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ fontWeight: 760 }}>Corpus by authority tier</div>
            <div className="muted" style={{ fontSize: 13 }}>
              BMSS tax research hierarchy: Tier 1 = statute, Tier 7 = commentary. Lower-tier sources are weighted higher in retrieval and the answer model is instructed to lead with them.
            </div>
          </div>
          <div>
            <button className="btn" type="button" onClick={onReload}>
              <RefreshCw size={14} /> Refresh
            </button>
          </div>
        </div>
      </div>
      <div className="bd">
        {error && <div className="muted" style={{ color: ERROR_TEXT }}>Error: {error}</div>}
        {!corpus && !error && <div className="muted">Loading…</div>}
        {corpus && (
          <>
            <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
              {formatNumber(corpus.total_documents)} documents · {formatNumber(corpus.total_chunks)} chunks
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {corpus.by_tier.map((bucket) => (
                <div key={bucket.tier} style={{ border: CARD_BORDER, borderRadius: 10, padding: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{
                      display: 'inline-block', minWidth: 24, padding: '2px 8px', borderRadius: 4,
                      fontWeight: 700, color: 'var(--bg)', background: tierColor(bucket.tier),
                    }}>
                      T{bucket.tier}
                    </span>
                    <span style={{ fontWeight: 600 }}>{bucket.label}</span>
                    <span className="muted" style={{ fontSize: 13 }}>
                      {formatNumber(bucket.count)} document{bucket.count === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {Object.entries(bucket.document_types).map(([dt, count]) => (
                      <button
                        key={dt}
                        type="button"
                        className="btn"
                        onClick={() => onFilterDocType(dt)}
                        title={`Filter inventory to ${dt}`}
                        style={{ fontSize: 11, padding: '2px 8px' }}
                      >
                        {dt} <span className="muted">({formatNumber(count)})</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {corpus.unknown_document_types?.length > 0 && (
              <div className="muted" style={{ fontSize: 12, marginTop: 10, color: WARN_TEXT }}>
                ⚠ {corpus.unknown_document_types.length} document type(s) not mapped to a tier — defaulted to Tier 7 (commentary). Add them to api/shared/taxRagAuthority.js to promote: {corpus.unknown_document_types.slice(0, 5).join(', ')}{corpus.unknown_document_types.length > 5 ? '…' : ''}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

// ── Tab: Inspector ───────────────────────────────────────────────────────────

function InspectorTab() {
  const [query, setQuery] = useState('');
  const [result, setResult] = useState<TaxRagInspectResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'final' | 'passA' | 'passB' | 'passC' | 'pool'>('final');
  const [tierFilter, setTierFilter] = useState<number | 'all'>('all');

  async function run() {
    const q = query.trim();
    if (!q) return;
    setLoading(true); setError(null); setResult(null);
    try {
      const r = await inspectTaxRagRetrieval(q);
      setResult(r);
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      run();
    }
  }

  const visibleList: TaxRagInspectChunk[] = useMemo(() => {
    if (!result) return [];
    let list: TaxRagInspectChunk[] = [];
    if (view === 'final')   list = result.finalSelection;
    else if (view === 'passA') list = result.passA;
    else if (view === 'passB') list = result.passB;
    else if (view === 'passC') list = result.passC;
    else if (view === 'pool')  list = result.candidatePool;
    if (tierFilter !== 'all') list = list.filter((c) => c.authority_tier === tierFilter);
    return list;
  }, [result, view, tierFilter]);

  return (
    <>
      <section className="card">
        <div className="hd">
          <div className="row">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ fontWeight: 760 }}>
                <Search size={14} style={{ verticalAlign: -2, marginRight: 6 }} />
                Retrieval Inspector
              </div>
              <div className="muted" style={{ fontSize: 13 }}>
                Run a query through the exact two-pass retrieval AskKeith uses. See every
                chunk surfaced by Pass A (general) and Pass B (primary-authority filter),
                their raw + boosted scores, authority tier, and content preview. Useful for
                diagnosing &quot;why didn&apos;t the §168 chunk make it?&quot; questions without changing
                the pipeline.
              </div>
            </div>
          </div>
        </div>
        <div className="bd">
          <textarea
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Paste a query — e.g. Are roof repairs eligible for bonus depreciation in a commercial real estate partnership?"
            rows={3}
            style={{ width: '100%', fontFamily: 'inherit', fontSize: 13, padding: 8, borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)' }}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="btn primary" type="button" onClick={run} disabled={loading || !query.trim()}>
              <Search size={14} /> {loading ? 'Inspecting…' : 'Inspect'}
            </button>
            <span className="muted" style={{ fontSize: 12 }}>Cmd/Ctrl-Enter to run.</span>
          </div>
          {error && <div className="muted" style={{ color: ERROR_TEXT, marginTop: 10 }}>Error: {error}</div>}
        </div>
      </section>

      {result && (
        <>
          <section className="card">
            <div className="bd">
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 13 }}>
                <span><strong>Embedding model:</strong> <code>{result.embeddingModel}</code></span>
                <span><strong>Confidence:</strong> <span style={{ color: statusColor(result.confidence) }}>● {result.confidence}</span></span>
                <span><strong>Top score:</strong> {result.topScore.toFixed(4)}</span>
                <span><strong>Avg top-5:</strong> {result.avgScore.toFixed(4)}</span>
                <span><strong>Strong:</strong> {result.strongCount}</span>
              </div>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 13, marginTop: 6 }}>
                <span><strong>Pass A:</strong> {result.passCounts.passA} chunks</span>
                <span><strong>Pass B:</strong> {result.passCounts.passB} chunks ({result.passCounts.passBOnly} new vs A)</span>
                <span><strong>Pass C (Tier 1 only):</strong> {result.passCounts.passC} chunks ({result.passCounts.passCOnly} new vs A+B)</span>
                <span><strong>Pool:</strong> {result.candidatePool.length}</span>
                <span><strong>Final:</strong> {result.finalSelection.length} → LLM</span>
              </div>
              <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {[1, 2, 3, 4].map((t) => {
                  const slot = result.tierSlots[t];
                  const color = slot ? 'var(--good)' : 'var(--warn)';
                  return (
                    <span key={t} style={{
                      padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                      color: 'var(--bg)', background: color,
                    }}>
                      T{t} quota: {slot ? (slot.document_type || 'filled') : 'EMPTY'}
                    </span>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="card">
            <div className="hd">
              <div className="row">
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {([
                    ['final', `Final → LLM (${result.finalSelection.length})`],
                    ['pool',  `Candidate pool (${result.candidatePool.length})`],
                    ['passA', `Pass A (${result.passA.length})`],
                    ['passB', `Pass B (${result.passB.length})`],
                    ['passC', `Pass C — Tier 1 only (${result.passC.length})`],
                  ] as const).map(([k, label]) => (
                    <button
                      key={k}
                      type="button"
                      className={view === k ? 'btn primary' : 'btn'}
                      onClick={() => setView(k as typeof view)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div>
                  <label className="muted" style={{ fontSize: 13, marginRight: 6 }}>Tier:</label>
                  <select value={tierFilter} onChange={(e) => setTierFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}>
                    <option value="all" style={OPTION_STYLE}>All tiers</option>
                    {[1, 2, 3, 4, 5, 6, 7].map((t) => (
                      <option key={t} value={t} style={OPTION_STYLE}>Tier {t}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="bd" style={{ padding: 0 }}>
              {visibleList.length === 0 && (
                <div className="muted" style={{ padding: 12 }}>No chunks match the current filter.</div>
              )}
              {visibleList.map((c, i) => (
                <ChunkRow key={`${c.id}-${i}`} chunk={c} viewKey={view} />
              ))}
            </div>
          </section>
        </>
      )}
    </>
  );
}

function ChunkRow({ chunk, viewKey }: { chunk: TaxRagInspectChunk; viewKey: 'final' | 'passA' | 'passB' | 'passC' | 'pool' }) {
  const tierColor = (tier: number) => {
    if (tier <= 2) return 'var(--good)';
    if (tier <= 4) return 'var(--accent)';
    if (tier <= 6) return 'var(--warn)';
    return 'var(--muted)';
  };
  return (
    <div style={{ borderBottom: CARD_BORDER, padding: '10px 12px' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600, fontSize: 13 }}>#{chunk.rank}</span>
        <span style={{
          padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 700,
          color: 'var(--bg)', background: tierColor(chunk.authority_tier),
        }}>
          T{chunk.authority_tier} {chunk.authority_label}
        </span>
        <span style={{ fontSize: 13 }}>
          <strong>{chunk.document_type || '(no doc type)'}</strong>
          {chunk.citation && <> · {chunk.citation}</>}
        </span>
        {viewKey === 'final' && chunk.source && (
          <span style={{
            padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600,
            background: chunk.source === 'quota' ? 'rgba(108, 194, 74, 0.15)' : 'rgba(255,255,255,0.05)',
            color: chunk.source === 'quota' ? 'var(--good)' : 'var(--muted)',
          }}>
            {chunk.source === 'quota' ? `QUOTA T${chunk.quotaTier}` : 'BACKFILL'}
          </span>
        )}
        {viewKey === 'passB' && chunk.also_in_pass_a !== undefined && (
          <span style={{
            padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600,
            background: chunk.also_in_pass_a ? 'rgba(255,255,255,0.05)' : 'rgba(0, 188, 212, 0.15)',
            color: chunk.also_in_pass_a ? 'var(--muted)' : 'var(--accent)',
          }}>
            {chunk.also_in_pass_a ? 'also in A' : 'NEW from B'}
          </span>
        )}
        {viewKey === 'passC' && (
          <span style={{
            padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600,
            background: (chunk.also_in_pass_a || chunk.also_in_pass_b) ? 'rgba(255,255,255,0.05)' : 'rgba(0, 188, 212, 0.15)',
            color: (chunk.also_in_pass_a || chunk.also_in_pass_b) ? 'var(--muted)' : 'var(--accent)',
          }}>
            {chunk.also_in_pass_a && chunk.also_in_pass_b ? 'also in A+B'
              : chunk.also_in_pass_a ? 'also in A'
              : chunk.also_in_pass_b ? 'also in B'
              : 'NEW from C'}
          </span>
        )}
        {viewKey === 'pool' && chunk.sources && (
          <span style={{ fontSize: 11, color: 'var(--muted)' }}>from {chunk.sources.join('+')}</span>
        )}
        <span className="muted" style={{ fontSize: 12, marginLeft: 'auto' }}>
          raw={chunk.raw_score.toFixed(4)} · boosted={chunk.boosted_score.toFixed(4)}
        </span>
      </div>
      {chunk.headline && (
        <div style={{ fontSize: 13, marginTop: 4 }}>{chunk.headline}</div>
      )}
      <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
        chunk {chunk.chunk_ordinal} ({chunk.chunk_type || 'body'}) · modified {chunk.content_modified_at ? formatDate(chunk.content_modified_at) : '—'} · length {formatNumber(chunk.content_length)} chars
      </div>
      <pre style={{
        marginTop: 6, padding: 8, borderRadius: 4,
        background: 'rgba(255,255,255,0.03)', fontSize: 11, lineHeight: 1.4,
        whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 160, overflow: 'auto',
        fontFamily: 'inherit',
      }}>{chunk.content_excerpt}{chunk.content_length > chunk.content_excerpt.length ? '…' : ''}</pre>
    </div>
  );
}

// ── Tab: Failed items ────────────────────────────────────────────────────────

function FailedItemsTab() {
  const [items, setItems] = useState<TaxRagFailedItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResolved, setShowResolved] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await listTaxRagFailedItems({ limit: 200, unresolved: !showResolved });
      setItems(res.items);
    } catch (e: any) { setError(e?.message || String(e)); }
    finally { setLoading(false); }
  }, [showResolved]);

  useEffect(() => { reload(); }, [reload]);

  return (
    <section className="card">
      <div className="hd">
        <div className="row">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ fontWeight: 760 }}>Failed items</div>
            <div className="muted" style={{ fontSize: 13 }}>Per-item errors from the ingestion pipeline. One newsItem failing does not abort the run.</div>
          </div>
        </div>
      </div>
      <div className="bd">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
          <label style={{ fontSize: 13 }}>
            <input type="checkbox" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} /> Include resolved
          </label>
          <button className="btn" type="button" onClick={reload}><RefreshCw size={14} /> Refresh</button>
        </div>

        {error && <div className="muted" style={{ color: ERROR_TEXT }}>Error: {error}</div>}
        {loading && !items.length && <div className="muted">Loading…</div>}
        {!loading && !items.length && !error && <div className="muted">No failed items.</div>}

        {items.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table className="tx-rag-table">
              <thead>
                <tr><th>When</th><th>Stage</th><th>GUID</th><th>Ver</th><th>Error</th><th>Resolved</th></tr>
              </thead>
              <tbody>
                {items.map((f) => (
                  <tr key={f.failed_item_id}>
                    <td>{formatDate(f.occurred_at)}</td>
                    <td>{f.stage}</td>
                    <td style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }} title={f.guid || ''}>{f.guid || '—'}</td>
                    <td>{f.version ?? '—'}</td>
                    <td style={{ maxWidth: 420 }}>{f.error_message}</td>
                    <td>{f.resolved_at ? formatDate(f.resolved_at) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <TableStyles />
      </div>
    </section>
  );
}

// ── Tab: Config ──────────────────────────────────────────────────────────────

function ConfigTab() {
  const [status, setStatus] = useState<TaxRagIndexStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [provisioning, setProvisioning] = useState(false);
  const [provisionMsg, setProvisionMsg] = useState<string | null>(null);
  const [model, setModel] = useState<'text-embedding-3-small' | 'text-embedding-3-large'>('text-embedding-3-small');

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try { setStatus(await getTaxRagIndexStatus()); }
    catch (e: any) { setError(e?.message || String(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  async function provision() {
    setProvisioning(true); setProvisionMsg(null); setError(null);
    try {
      const r = await provisionTaxRagIndex(model);
      setProvisionMsg(`${r.created ? 'Created' : 'Updated'} index "${r.indexName}" (${r.vectorDimensions}d for ${r.embeddingModel}).`);
      await reload();
    } catch (e: any) { setError(e?.message || String(e)); }
    finally { setProvisioning(false); }
  }

  return (
    <>
      <section className="card">
        <div className="hd"><div className="row"><div style={{ fontWeight: 760 }}>Azure AI Search status</div></div></div>
        <div className="bd">
          {loading && <div className="muted">Loading…</div>}
          {error && <div className="muted" style={{ color: ERROR_TEXT }}>Error: {error}</div>}
          {status && !status.configured && (
            <div>
              <div className="muted" style={{ color: WARN_TEXT }}>Not configured.</div>
              <div className="muted" style={{ marginTop: 6 }}>{status.hint}</div>
            </div>
          )}
          {status && status.configured && (
            <div className="kv">
              <div className="field"><div className="k">Endpoint</div>        <div className="v" style={{ wordBreak: 'break-all' }}>{status.endpoint || '—'}</div></div>
              <div className="field"><div className="k">Index</div>           <div className="v">{status.indexName}</div></div>
              <div className="field"><div className="k">Index exists</div>    <div className="v">{status.exists === false ? 'No (provision required)' : 'Yes'}</div></div>
              <div className="field"><div className="k">Documents</div>       <div className="v">{status.documentCount != null ? formatNumber(status.documentCount) : '—'}</div></div>
              <div className="field"><div className="k">Storage size</div>    <div className="v">{formatBytes(status.storageSize)}</div></div>
              <div className="field"><div className="k">Vector dims</div>     <div className="v">{status.vectorDimensions ?? '—'}</div></div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 14, alignItems: 'center', flexWrap: 'wrap' }}>
            <label className="muted" style={{ fontSize: 13 }}>Model:</label>
            <select value={model} onChange={(e) => setModel(e.target.value as any)}>
              <option value="text-embedding-3-small" style={OPTION_STYLE}>text-embedding-3-small (1536d)</option>
              <option value="text-embedding-3-large" style={OPTION_STYLE}>text-embedding-3-large (3072d)</option>
            </select>
            <button className="btn" type="button" disabled={provisioning} onClick={provision}>
              {provisioning ? 'Provisioning…' : 'Create / update index'}
            </button>
            <button className="btn" type="button" onClick={reload}><RefreshCw size={14} /> Reload</button>
          </div>
          {provisionMsg && <div className="muted" style={{ marginTop: 10 }}>{provisionMsg}</div>}
        </div>
      </section>

      <section className="card">
        <div className="hd"><div className="row"><div style={{ fontWeight: 760 }}>Required Function App settings</div></div></div>
        <div className="bd">
          <div className="muted" style={{ fontSize: 13, marginBottom: 10 }}>
            Set these on the Function App (Azure Portal → your-function-app → Configuration) or in local <code>api/local.settings.json</code> for dev.
          </div>
          <table className="tx-rag-table">
            <thead><tr><th>Env var</th><th>Purpose</th></tr></thead>
            <tbody>
              <tr><td><code>OPENAI_API_KEY</code></td><td>Embeddings</td></tr>
              <tr><td><code>TAX_RAG_EMBEDDING_MODEL</code></td><td>Default model (optional, defaults to text-embedding-3-small)</td></tr>
              <tr><td><code>AZURE_SEARCH_ENDPOINT</code></td><td>e.g. https://aisearch-bmss-prod-001.search.windows.net</td></tr>
              <tr><td><code>AZURE_SEARCH_ADMIN_KEY</code></td><td>Admin key from Keys blade</td></tr>
              <tr><td><code>AZURE_SEARCH_INDEX_NAME</code></td><td>Defaults to tax-authority-v1</td></tr>
              <tr><td><code>TAX_RAG_TRIGGER_KEY</code></td><td>Shared secret for Logic App → /api/tax-rag-run via x-trigger-key header</td></tr>
              <tr><td><code>TAX_RAG_ALLOWED_HOSTS</code></td><td>Comma-separated additional source hosts (Blob is already allowed)</td></tr>
              <tr><td><code>SQL_SERVER</code> / <code>SQL_DATABASE</code> / <code>SQL_USER</code> / <code>SQL_PASSWORD</code></td><td>Existing SQL connection (already in use)</td></tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="card">
        <div className="hd"><div className="row"><div style={{ fontWeight: 760 }}>Logic App trigger</div></div></div>
        <div className="bd">
          <div className="muted" style={{ fontSize: 13, marginBottom: 10 }}>
            SWA's managed Functions don't support timer triggers. Schedule your ingestion by
            adding an <strong>HTTP action</strong> in your existing Logic App that POSTs to:
          </div>
          <pre style={{ padding: 10, background: 'rgba(255,255,255,0.04)', borderRadius: 6, fontSize: 12, overflowX: 'auto' }}>
{`POST https://<your-swa-hostname>/api/tax-rag-run
Content-Type: application/json
x-trigger-key: <value of TAX_RAG_TRIGGER_KEY>

{
  "sourceUrl": "https://storageacctbmssprod001.blob.core.windows.net/container-bmssprod001-public/taxNotes/taxanalysts-Today-live-<timestamp>.zip",
  "sourceName": "Daily Today feed",
  "mode": "full"
}`}
          </pre>
          <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
            For the "Today" and Corrections feeds you'll want two actions on your schedule
            (every 15 min is fine — the pipeline skips zips whose file hash is already
            recorded in the runs table). Rotate the trigger key by updating the Function App
            setting and the Logic App action together.
          </div>
        </div>
      </section>
    </>
  );
}

// ── Shared table styles ──────────────────────────────────────────────────────

function TableStyles() {
  return (
    <style>{`
      table.tx-rag-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 13px;
      }
      table.tx-rag-table th, table.tx-rag-table td {
        padding: 6px 10px;
        border-bottom: 1px solid var(--border);
        white-space: nowrap;
      }
      table.tx-rag-table th {
        text-align: left;
        font-weight: 600;
        color: var(--muted);
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.03em;
      }
      table.tx-rag-table code {
        font-size: 12px;
      }
    `}</style>
  );
}
